// LateFeePolicyTab — the entire "Late Fee Policy" tab content. Sprint 5d.
//
// Owns its own Antd Form instance + the calc-type radio (which controls
// whether the FLAT or PERCENTAGE/COMPOUNDING field is shown). The
// orchestrator just hands in:
//   - the current policy (read; null when none configured)
//   - the loading / saving / deleting flags from useFeesData
//   - onSave / onDelete callbacks that wrap the hook actions with toast
//
// This was the most tangled part of Fees.tsx because of the dynamic
// FLAT-vs-PERCENTAGE field. Here it's local state, scoped to one file.
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Col, Form, InputNumber, Row, Select, Space, Spin, Switch,
} from 'antd';
import {
  ClockCircleOutlined, DollarOutlined, PercentageOutlined, StopOutlined,
} from '@ant-design/icons';
import type { LateFeeCalculationType, LateFeePolicy } from '../../types';

export interface LateFeePolicyTabProps {
  policy: LateFeePolicy | null;
  loading: boolean;
  saving: boolean;
  deleting: boolean;
  onSave: (values: LateFeePolicyFormValues) => Promise<void> | void;
  onDelete: () => void;
}

export interface LateFeePolicyFormValues {
  gracePeriodDays: number;
  calculationType: LateFeeCalculationType;
  flatAmount?:     number;
  percentageRate?: number;
  maxCap?:         number;
  isActive:        boolean;
}

export const LateFeePolicyTab: React.FC<LateFeePolicyTabProps> = ({
  policy, loading, saving, deleting, onSave, onDelete,
}) => {
  const [form] = Form.useForm();
  const [calcType, setCalcType] = useState<LateFeeCalculationType>('FLAT');

  // The backend now always returns a policy object: a persisted row (has an
  // `id`) or a non-persisted sensible default (id null/absent, isActive=false)
  // so the form can pre-fill. Treat "no id" as "not configured yet" so the
  // info banner, the Create-vs-Update label, and the Delete button stay correct.
  const isPersisted = !!policy?.id;

  // Hydrate the form when the hook resolves the policy. Idempotent —
  // re-firing on the same policy reference is a no-op for Antd Form.
  useEffect(() => {
    if (policy) {
      setCalcType(policy.calculationType);
      form.setFieldsValue({
        gracePeriodDays: policy.gracePeriodDays,
        calculationType: policy.calculationType,
        flatAmount:      policy.flatAmount,
        percentageRate:  policy.percentageRate,
        maxCap:          policy.maxCap,
        isActive:        policy.isActive,
      });
    }
  }, [policy, form]);

  return (
    <Spin spinning={loading}>
      {isPersisted && policy && (
        <Alert
          type={policy.isActive ? 'success' : 'warning'}
          showIcon
          message={
            policy.isActive
              ? `Active policy: ${policy.calculationType} — ${policy.gracePeriodDays} day grace period`
              : 'Policy exists but is currently disabled'
          }
          style={{ marginBottom: 20 }}
        />
      )}
      {!isPersisted && !loading && (
        <Alert
          type="info"
          showIcon
          message="No late fee policy configured. Create one below to automatically charge late fees."
          style={{ marginBottom: 20 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={onSave}
        initialValues={{ gracePeriodDays: 5, calculationType: 'FLAT', isActive: true }}
      >
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Form.Item
              name="gracePeriodDays"
              label={<span><ClockCircleOutlined /> Grace Period (days)</span>}
              rules={[{ required: true, message: 'Required' }]}
              tooltip="Days after due date before late fee is charged"
            >
              <InputNumber min={0} max={90} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="calculationType"
              label="Calculation Type"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select onChange={(v: LateFeeCalculationType) => setCalcType(v)}>
                <Select.Option value="FLAT">Flat Amount</Select.Option>
                <Select.Option value="PERCENTAGE">Percentage of Outstanding</Select.Option>
                <Select.Option value="COMPOUNDING">Compounding (daily %)</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="isActive" label="Status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          {calcType === 'FLAT' && (
            <Col xs={24} md={8}>
              <Form.Item
                name="flatAmount"
                label={<span><DollarOutlined /> Flat Amount ($)</span>}
                rules={[{ required: true, message: 'Required for FLAT type' }]}
              >
                <InputNumber min={0} step={0.01} prefix="$" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          {(calcType === 'PERCENTAGE' || calcType === 'COMPOUNDING') && (
            <Col xs={24} md={8}>
              <Form.Item
                name="percentageRate"
                label={<span><PercentageOutlined /> Rate (%)</span>}
                rules={[{ required: true, message: 'Required for this type' }]}
                tooltip={
                  calcType === 'COMPOUNDING'
                    ? 'Applied daily to the growing balance'
                    : 'Applied once to the outstanding amount'
                }
              >
                <InputNumber min={0} max={100} step={0.1} suffix="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={8}>
            <Form.Item
              name="maxCap"
              label="Max Cap ($, optional)"
              tooltip="Maximum total late fee that can accumulate per original fee"
            >
              <InputNumber min={0} step={0.01} prefix="$" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>
            {isPersisted ? 'Update Policy' : 'Create Policy'}
          </Button>
          {isPersisted && (
            <Button danger icon={<StopOutlined />} onClick={onDelete} loading={deleting}>
              Delete Policy
            </Button>
          )}
        </Space>
      </Form>
    </Spin>
  );
};
